import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MoreVertical } from "lucide-react";
import { api } from "../../lib/api";
import { Input } from "../ui/input";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../ui/dropdown-menu";
import { toast } from "../ui/sonner";
import { StatusBadge } from "./StatusBadge";
import { ReasonModal } from "./ReasonModal";
import { ConfirmDialog } from "./ConfirmDialog";
import { DateFilterPicker } from "./DateFilterPicker";
import { formatDate } from "../../lib/dateUtils";

const ROLE_FILTERS = [
  { value: "", label: "All Roles" },
  { value: "stranger", label: "Strangers" },
  { value: "photographer", label: "Photographers" },
  { value: "admin", label: "Admins" },
];

const STATUS_FILTERS = [
  { value: "", label: "All Statuses" },
  { value: "active", label: "Active" },
  { value: "suspended", label: "Suspended" },
  { value: "banned", label: "Banned" },
  { value: "deleted", label: "Deleted" },
];

export const UsersTab = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState(null);
  const [role, setRole] = useState("");
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [reasonModal, setReasonModal] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null);

  const load = () => {
    api
      .get("/admin/users", { params: { role: role || undefined, status: status || undefined, search: search || undefined, date_from: dateFrom || undefined, date_to: dateTo || undefined } })
      .then(({ data }) => setUsers(data));
  };

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, status, search, dateFrom, dateTo]);

  const setUserStatus = async (userId, newStatus, reason) => {
    try {
      await api.put(`/admin/users/${userId}/status`, { status: newStatus, reason });
      toast.success("User status updated");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't update status");
    }
  };

  return (
    <div data-testid="admin-users-tab">
      <div className="flex flex-wrap items-end gap-3 mb-4">
        {ROLE_FILTERS.map((f) => (
          <button
            key={f.value}
            data-testid={`user-role-filter-${f.value || "all"}`}
            onClick={() => setRole(f.value)}
            className={`text-xs font-bold tracking-widest uppercase px-4 py-2 rounded-full border-2 transition-colors ${role === f.value ? "bg-midnight-black text-clean-white border-midnight-black" : "border-soft-gray text-midnight-black/70"}`}
          >
            {f.label}
          </button>
        ))}
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.value}
            data-testid={`user-status-filter-${f.value || "all"}`}
            onClick={() => setStatus(f.value)}
            className={`text-xs font-bold tracking-widest uppercase px-4 py-2 rounded-full border-2 transition-colors ${status === f.value ? "bg-stranger-orange text-clean-white border-stranger-orange" : "border-soft-gray text-midnight-black/70"}`}
          >
            {f.label}
          </button>
        ))}
      </div>
      <div className="flex flex-wrap items-end gap-3 mb-6">
        <Input data-testid="user-search-input" placeholder="Search name, email, or user ID..." value={search} onChange={(e) => setSearch(e.target.value)} className="max-w-xs" />
        <DateFilterPicker label="Joined From" testId="user-date-from-input" value={dateFrom} onChange={setDateFrom} />
        <DateFilterPicker label="Joined To" testId="user-date-to-input" value={dateTo} onChange={setDateTo} />
      </div>

      {users === null ? (
        <p className="font-medium text-midnight-black/60">Loading users...</p>
      ) : users.length === 0 ? (
        <p className="font-medium text-midnight-black/60" data-testid="no-users">No users match.</p>
      ) : (
        <div className="space-y-3" data-testid="users-list">
          {users.map((u) => (
            <div key={u.id} className="border border-soft-gray rounded-xl p-4 flex items-center justify-between gap-4 flex-wrap" data-testid={`user-row-${u.id}`}>
              <div className="cursor-pointer flex-1 min-w-[200px]" onClick={() => navigate(`/admin/users/${u.id}`)} data-testid={`user-row-link-${u.id}`}>
                <p className="font-extrabold text-sm uppercase">
                  {u.first_name} {u.last_name}
                </p>
                <p className="text-xs font-medium text-midnight-black/60">{u.email} · {u.role}</p>
                {u.role === "photographer" && <p className="text-xs font-medium text-midnight-black/50">{u.business_name} · verification: {u.verification_status || "not submitted"}</p>}
                <p className="text-[10px] font-bold uppercase tracking-wide text-midnight-black/40 mt-1">Joined {formatDate(u.created_at)}</p>
              </div>
              <div className="flex items-center gap-3">
                <StatusBadge status={u.status} testId={`user-status-${u.id}`} />
                {u.role !== "super_admin" && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className="p-1.5 rounded-full border border-soft-gray hover:border-stranger-orange" data-testid={`user-actions-menu-${u.id}`} aria-label="User actions">
                        <MoreVertical className="w-4 h-4" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" data-testid={`user-actions-content-${u.id}`}>
                      {u.status !== "active" && (
                        <DropdownMenuItem data-testid={`action-activate-${u.id}`} onClick={() => setConfirmDialog({ userId: u.id, action: "active", label: "Activate" })}>
                          Activate
                        </DropdownMenuItem>
                      )}
                      {u.status !== "suspended" && (
                        <DropdownMenuItem data-testid={`action-suspend-${u.id}`} onClick={() => setReasonModal({ userId: u.id, action: "suspended", label: "Suspend" })}>
                          Suspend
                        </DropdownMenuItem>
                      )}
                      {u.status !== "banned" && (
                        <DropdownMenuItem data-testid={`action-ban-${u.id}`} onClick={() => setReasonModal({ userId: u.id, action: "banned", label: "Ban" })}>
                          Ban
                        </DropdownMenuItem>
                      )}
                      {u.status !== "deleted" && (
                        <DropdownMenuItem data-testid={`action-delete-${u.id}`} className="text-red-600" onClick={() => setConfirmDialog({ userId: u.id, action: "deleted", label: "Delete" })}>
                          Delete
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <ReasonModal
        open={!!reasonModal}
        onOpenChange={(v) => !v && setReasonModal(null)}
        title={`${reasonModal?.label} this account`}
        description="This action is logged. Please provide a reason."
        confirmLabel={reasonModal?.label?.toUpperCase()}
        onConfirm={(reason) => setUserStatus(reasonModal.userId, reasonModal.action, reason)}
      />
      <ConfirmDialog
        open={!!confirmDialog}
        onOpenChange={(v) => !v && setConfirmDialog(null)}
        title={`${confirmDialog?.label} this account?`}
        description={confirmDialog?.action === "deleted" ? "This soft-deletes the account. Transactional history is preserved and this can be undone." : "This will restore full account access."}
        confirmLabel={confirmDialog?.label?.toUpperCase()}
        destructive={confirmDialog?.action === "deleted"}
        onConfirm={() => setUserStatus(confirmDialog.userId, confirmDialog.action)}
      />
    </div>
  );
};
